import React, { useEffect } from 'react'
import { View, TouchableOpacity, Text, Animated, Platform, StyleSheet, Dimensions } from 'react-native'
import { Feather } from '@expo/vector-icons'
import theme from '../../../theme'

const { width: screenWidth } = Dimensions.get('window')

/**
 * RightSidebar - Sidebar deslizable desde la derecha
 * Se muestra sobre el contenido sin desplazarlo
 *
 * @param {boolean} isOpen - Controla si el sidebar está abierto o cerrado
 * @param {function} onClose - Callback al cerrar el sidebar
 * @param {string} title - Título del sidebar
 * @param {React.ReactNode} children - Contenido del sidebar
 * @param {number} width - Ancho del sidebar (default: 400px)
 * @param {number} topOffset - Offset desde el top (default: 60px para el header)
 */
const RightSidebar = ({ isOpen, onClose, title, children, width = 400, topOffset = 60, overlayPointerEvents = 'auto' }) => {
    const slideAnim = React.useRef(new Animated.Value(width)).current

    useEffect(() => {
        Animated.spring(slideAnim, {
            toValue: isOpen ? 0 : width,
            useNativeDriver: true,
            tension: 80,
            friction: 20,
        }).start()
    }, [isOpen, width])

    // Bloquear scroll del body cuando el sidebar está abierto
    useEffect(() => {
        if (Platform.OS === 'web') {
            if (isOpen) {
                // Guardar el scroll actual
                const scrollY = window.scrollY
                document.body.style.position = 'fixed'
                document.body.style.top = `-${scrollY}px`
                document.body.style.width = '100%'
                document.body.style.overflowY = 'hidden'
            } else {
                // Restaurar el scroll
                const scrollY = document.body.style.top
                document.body.style.position = ''
                document.body.style.top = ''
                document.body.style.width = ''
                document.body.style.overflowY = ''
                window.scrollTo(0, parseInt(scrollY || '0') * -1)
            }
        }

        // Cleanup cuando se desmonte el componente
        return () => {
            if (Platform.OS === 'web') {
                document.body.style.position = ''
                document.body.style.top = ''
                document.body.style.width = ''
                document.body.style.overflowY = ''
            }
        }
    }, [isOpen])

    if (!isOpen && Platform.OS === 'web') {
        return null // No renderizar nada cuando está cerrado en web
    }

    return (
        <>
            {/* Overlay oscuro */}
            {isOpen && (
                <TouchableOpacity
                    style={[
                        styles.overlay,
                        {
                            top: topOffset, // ← Empieza debajo del header
                            height: `calc(100vh - ${topOffset}px)`, // ← Altura ajustada
                        },
                    ]}
                    pointerEvents={overlayPointerEvents}
                    onPress={onClose}
                    activeOpacity={1}
                />
            )}

            {/* Sidebar */}
            <Animated.View
                style={[
                    styles.sidebar,
                    {
                        width,
                        top: topOffset, // ← Usa el topOffset para empezar debajo del header
                        height: `calc(100vh - ${topOffset}px)`, // ← Altura ajustada
                        transform: [{ translateX: slideAnim }],
                    },
                ]}
            >
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.title}>{title}</Text>
                    <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                        <Feather name='x' size={24} color={theme.colors.textPrimary} />
                    </TouchableOpacity>
                </View>

                {/* Content - Contenedor con altura calculada */}
                <View style={styles.contentWrapper}>{children}</View>
            </Animated.View>
        </>
    )
}

const styles = StyleSheet.create({
    overlay: {
        position: 'fixed', // ← FIXED para cubrir todo el viewport
        // top y height se pasan como inline styles
        left: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 999,
    },
    sidebar: {
        position: 'fixed', // ← FIXED - siempre en viewport
        // top y height se pasan como inline styles
        right: 0,
        backgroundColor: theme.colors.background || '#FFFFFF',
        boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.25)',
        elevation: 10,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        // overflow: 'hidden' removido temporalmente para debugging
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 18,
        borderBottomWidth: 1,
        marginBottom: 8,
        borderBottomColor: theme.colors.border || '#E5E5E5',
        backgroundColor: theme.colors.background || '#FFFFFF',
        flexShrink: 0, // No se encoge
        minHeight: 64, // Altura mínima garantizada (aumentado)
        zIndex: 10, // Asegura que esté sobre el content (aumentado)
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: theme.colors.textPrimary,
    },
    closeButton: {
        padding: 8,
    },
    contentWrapper: {
        flex: 1,
        backgroundColor: theme.colors.background || '#FFFFFF',
        position: 'relative', // Permite posicionamiento absoluto de hijos
        minHeight: 0, // Permite que se encoja para dar espacio al header
        overflow: 'hidden', // El overflow va aquí, no en el sidebar
    },
})

export default RightSidebar
